import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Congress } from "@/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useDeleteCongress } from "@/features/congress-details/api/delete-congress";
import { congressMetaQueryKey } from "@/features/congress-details/api/get-congress";
import { congressesQueryKey } from "@/features/congress-select/api/use-congresses";
import { useCongress } from "@/features/congress-select/hooks/use-congress";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/libs/react-query";
import { useTranslation } from "react-i18next";

type DeleteCongressModalProps = {
  congress: Congress;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function DeleteCongressModal({
  congress,
  open,
  onOpenChange,
}: DeleteCongressModalProps) {
  const { t } = useTranslation();
  const [confirmName, setConfirmName] = useState("");
  const { setCongress } = useCongress();
  const { toast } = useToast();
  const navigate = useNavigate();
  const nameMatches = confirmName === congress.name;

  const { mutateAsync: deleteCongress, isPending } = useDeleteCongress(
    congress.id,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: congressesQueryKey });
        queryClient.removeQueries({
          queryKey: congressMetaQueryKey(congress.id),
        });
        setCongress(null as unknown as Congress);
        onOpenChange(false);
        setConfirmName("");
        navigate("/");
        toast({ title: t("deleteCongress.deleted") });
      },
      onError: (err) => {
        toast({
          title: t("deleteCongress.deleteFailed"),
          description: err.message,
          variant: "destructive",
        });
      },
    },
  );

  const handleDelete = () => {
    if (!nameMatches) return;
    deleteCongress(congress.name);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) setConfirmName("");
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("deleteCongress.title")}</DialogTitle>
          <DialogDescription>
            {t("deleteCongress.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-4">
          <Label htmlFor="confirm-name">
            {t("deleteCongress.confirmLabel")}{" "}
            <strong>{congress.name}</strong>
          </Label>
          <Input
            id="confirm-name"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={congress.name}
            className="font-mono"
            disabled={isPending}
            autoComplete="off"
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            {t("deleteCongress.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!nameMatches || isPending}
          >
            {isPending ? t("deleteCongress.deleting") : t("deleteCongress.delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { DeleteCongressModal };
